// =========================================================================
// Turn Indicator (W4)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   §「画面フロー」 上部: 残り手数 (e.g. "12 / 15")
//   §「線質差カタログ」 残り手数が少ない → --warn-soft で警告色
//
// 役割:
//   - 「movesUsed / N」 を上部 HUD に表示する pure UI
//   - 残り手数 (= N - movesUsed) <= 3 で警告色に切り替え
//   - controller が毎手後に update() を呼ぶ
//
// 注意:
//   - spec の文言例 "12 / 15" は「使用 / 上限」 表記。残り手数 ≠ 表示値だが、
//     警告判定には残り (N - movesUsed) を使う。
// =========================================================================

import { __ensureW4Styles } from './preview.js';

// -------------------------------------------------------------------------
// 公開 API
// -------------------------------------------------------------------------

export interface TurnIndicatorHandle {
  /** 毎手後 / 初期化時に controller が呼ぶ */
  update(movesUsed: number, N: number): void;
  /** DOM 解放 */
  destroy(): void;
}

/** 警告に切り替える残り手数しきい値 (spec: "3 以下なら --warn-soft") */
export const WARN_THRESHOLD = 3;

/**
 * container 内に turn-indicator を生成して返す。
 * 同じ container に複数 create された場合は重複生成する (呼び出し側で管理)。
 */
export function createTurnIndicator(container: HTMLElement): TurnIndicatorHandle {
  __ensureW4Styles();

  const root = document.createElement('div');
  root.className = 'cr-turn-indicator';
  root.setAttribute('role', 'status');
  root.setAttribute('aria-live', 'polite');
  root.setAttribute('aria-label', '残り手数');

  const used = document.createElement('span');
  used.className = 'cr-turn-indicator__used';

  const sep = document.createElement('span');
  sep.className = 'cr-turn-indicator__sep';
  sep.textContent = '/';

  const limit = document.createElement('span');
  limit.className = 'cr-turn-indicator__limit';

  root.appendChild(used);
  root.appendChild(sep);
  root.appendChild(limit);
  container.appendChild(root);

  // 初期値 (controller の update 前に何も出ないと不自然なので空相当を入れておく)
  used.textContent = '0';
  limit.textContent = '0';

  const handle: TurnIndicatorHandle = {
    update(movesUsed, N) {
      // 防御的: 不正値は表示崩れを避けるため整数化 + clamp
      const u = Math.max(0, Math.floor(Number.isFinite(movesUsed) ? movesUsed : 0));
      const n = Math.max(0, Math.floor(Number.isFinite(N) ? N : 0));
      used.textContent = String(u);
      limit.textContent = String(n);
      const remaining = n - u;
      const isWarn = remaining <= WARN_THRESHOLD && n > 0;
      root.setAttribute('data-warn', isWarn ? 'true' : 'false');
      root.setAttribute(
        'aria-label',
        `残り手数 ${Math.max(0, remaining)} 手 (${u} / ${n})`,
      );
    },
    destroy() {
      if (root.parentElement) root.parentElement.removeChild(root);
    },
  };
  return handle;
}
